import { useCallback, useMemo } from 'react'

import {
  HookReturnValue,
  Route,
  Switch,
  useRouter,
  Router as WouterRouter,
  matchRoute,
  useLocation,
  Redirect
} from 'wouter'

import {
  BrowserLocationHook,
  useBrowserLocation,
  useHistoryState
} from 'wouter/use-browser-location'

import { CheckPermission, Permission, Protected } from './features/auth'
import PageLayout from '@/layouts/Page'
import DialogLayout from '@/layouts/Dialog'

import Goodies from './pages/Goodies'
import CreateOrganization from './pages/CreateOrganization'
import SignIn from './pages/SignIn'
import SignUp from './pages/SignUp'

import CreateGoodie from './dialogs/CreateGoodie'
import Feed from './pages/Feed'
import Stays from './pages/Stays'
import Layout from './pages/Layout'
import Branding from './pages/Branding'
import Requests from './pages/Requests'
import EventList from './pages/Events'
import Settings from './pages/Settings'
import Analytics from './pages/Analytics'
import Support from './pages/Support'
import Integrations from './pages/Integrations'
import Profile from './dialogs/Profile'
import GoodieSuspense from './dialogs/Goodie'
import CreatePromotion from './dialogs/CreatePromotion'
import PromotionSuspense from './dialogs/Promotion'
import CreateCategory from './dialogs/CreateCategory'
import CategorySuspense from './dialogs/Category'
import AddGoodiesToCategory from './dialogs/AddGoodiesToCategory'
import EditEventOccurenceSuspense from './dialogs/EditEventOccurence'
import CreateStay from './dialogs/CreateStay'
import StaySuspense from './dialogs/Stay'
import StayOrdersSuspense from './dialogs/StayOrders'
import OrganizationMembers from './dialogs/OrganizationMembers'
import ResetPassword from './pages/ResetPassword'
import QrGenerator from './pages/QrCodeGenerator'
import Route404 from './pages/Route404'
import FeedbacksList from './pages/Feedbacks'
import Rooms from './pages/Rooms'
import SpaceSuspense from './dialogs/Space'
import CreateSpace from './dialogs/CreateSpace'
import Guests from './pages/Guests'
import GuestSuspense from './dialogs/Guest'
import IntegrationSuspense from './dialogs/Integration'
import OrganizationBilling from './dialogs/OrganizationBilling'
import ClaimOrganization from './pages/ClaimOrganization'
import OrganizationSettings from './dialogs/OrganizationSettings'

import CheckPms from './features/integrations/components/CheckPms'
import { APP_BASE, appPath } from '@/utils/appPath'

type SetLocationOptions = { replace?: boolean; state?: unknown }

enum DialogRoutes {
  organizationMembers = '/organization-members',
  organizationBilling = '/organization-billing',
  organizationSettings = '/organization-settings',
  profile = '/profile',
  createGoodie = '/create-goodie/:type?',
  goodie = '/goodie/:token',
  createPromotion = '/create-promotion/:goodieToken?',
  promotion = '/promotion/:token?',
  createCategory = '/create-category/:layoutVersion',
  category = '/category/:layoutVersion/:slug',
  addCategoryGoodies = '/add-category-goodies/:layoutVersion/:slug',
  editEventOccurence = '/edit-event-occurence/:token',
  createStay = '/create-stay',
  stay = '/stay/:token',
  createSpace = '/create-space',
  space = '/space/:identifier',
  stayOrders = '/stay-orders/:token',
  guest = '/guest/:token',
  integration = '/integration/:provider/:type'
}

const DEFAULT_PATH = appPath('/goodies')

export function useIsModal() {
  const { base, parser } = useRouter()

  return useCallback(
    (to: string | URL) => {
      const toString = to.toString().replace(base, '')

      return Object.values(DialogRoutes).some(route => {
        return matchRoute(parser, route, toString, true)[0]
      })
    },
    [parser, base]
  )
}

function useSetLocation(location: string) {
  const state = useHistoryState<{ background?: string } | null>()
  const [, defaultSetLocation] = useBrowserLocation()
  const isModal = useIsModal()

  return function setLocation(
    to: string | URL,
    opts: SetLocationOptions | undefined
  ) {
    const needsBackground = isModal(to)

    if (needsBackground) {
      defaultSetLocation(to, {
        ...(opts ?? {}),
        state: {
          ...(opts?.state ?? {}),
          // If we already are in a modal state we need to re-use the background from before
          background: state?.background ?? location,
          from: location
        }
      })
    } else {
      defaultSetLocation(to, {
        ...(opts ?? {}),
        state: {
          from: location,
          ...(opts?.state ?? {})
        }
      })
    }
  }
}

function usePageLocation(): HookReturnValue<BrowserLocationHook> {
  const state = useHistoryState<{ background?: string } | null>()
  const [defaultLocation] = useBrowserLocation()
  const isModal = useIsModal()

  const location = useMemo(() => {
    if (state?.background) {
      return state.background
    } else if (isModal(defaultLocation)) {
      return DEFAULT_PATH
    } else {
      return defaultLocation
    }
  }, [defaultLocation, state, isModal])

  const setLocation = useSetLocation(location)

  return [location, setLocation]
}

function useDialogLocation(): HookReturnValue<BrowserLocationHook> {
  const [location] = useBrowserLocation()
  const setLocation = useSetLocation(location)

  return [location, setLocation]
}

export function useCloseModal() {
  const { base } = useRouter()
  const [, navigate] = useLocation()
  const state = useHistoryState<{ background?: string } | null>()

  return useCallback(() => {
    const to = state?.background ?? appPath('/goodies')

    navigate(to.replace(base, ''))
  }, [base, navigate, state])
}

export function useHistoryFrom() {
  const state = useHistoryState<{
    from?: string
    background?: string
  }>()

  if (state.from) return state.from
}

export function useUpdateHistoryState() {
  return useCallback((patch: Record<string, unknown>) => {
    window.history.replaceState({ ...window.history.state, ...patch }, '')
  }, [])
}

/**
 * Helper for most of our Routes which are protected Routes that need and organization and use the default PageLayout
 */
function DefaultRoute({
  children,
  path
}: React.PropsWithChildren<{ path: string }>) {
  return (
    <Route path={path}>
      <Protected needsOrg>
        <PageLayout>{children}</PageLayout>
      </Protected>
    </Route>
  )
}

/**
 * Helper for most of our Dialog Routes which are protected Routes that need and organization and use the default DialogLayout
 */
function DefaultDialogRoute({
  children,
  path
}: React.PropsWithChildren<{ path: string }>) {
  return (
    <Route path={path}>
      <Protected needsOrg>
        <DialogLayout isOpen>{children}</DialogLayout>
      </Protected>
    </Route>
  )
}

export default function Router() {
  return (
    <WouterRouter>
      <WouterRouter base={APP_BASE} hook={usePageLocation}>
        {/* Routes that need to be signed in and have and Organization selected */}
        <Switch>
          <Route path="/">
            <Redirect to="/goodies" />
          </Route>
          <DefaultRoute path="/goodies">
            <Goodies />
          </DefaultRoute>
          <DefaultRoute path="/feed">
            <Feed />
          </DefaultRoute>
          <DefaultRoute path="/branding">
            <Branding />
          </DefaultRoute>
          <DefaultRoute path="/layout/:layoutVersion?/:categorySlug?">
            <Layout />
          </DefaultRoute>
          <DefaultRoute path="/requests">
            <Requests />
          </DefaultRoute>
          <DefaultRoute path="/events">
            <EventList />
          </DefaultRoute>
          <DefaultRoute path="/feedbacks">
            <FeedbacksList />
          </DefaultRoute>
          <DefaultRoute path="/stays">
            <Stays />
          </DefaultRoute>
          <DefaultRoute path="/rooms">
            <Rooms />
          </DefaultRoute>
          <DefaultRoute path="/analytics">
            <Analytics />
          </DefaultRoute>
          <DefaultRoute path="/qr-codes">
            <QrGenerator />
          </DefaultRoute>
          <DefaultRoute path="/settings">
            <Settings />
          </DefaultRoute>
          <DefaultRoute path="/support">
            <Support />
          </DefaultRoute>
          <DefaultRoute path="/guests">
            <Guests />
          </DefaultRoute>
          <DefaultRoute path="/integrations">
            <Integrations />
          </DefaultRoute>

          {/* Routes that need to be signed in but don't need an organization */}
          <Route path="/create-organization">
            <Protected needsOrg={false}>
              <CreateOrganization />
            </Protected>
          </Route>
          <Route path="/claim-organization/:slug/:secret">
            <Protected needsOrg={false}>
              <ClaimOrganization />
            </Protected>
          </Route>

          {/* Routes were you don't need to be SignedIn */}
          <Route path="/sign-in">
            <SignIn />
          </Route>
          <Route path="/sign-up">
            <SignUp />
          </Route>
          <Route path="/reset-password/:code">
            <ResetPassword />
          </Route>

          <Route>
            <Route404 />
          </Route>
        </Switch>
      </WouterRouter>

      {/* Routes that are dialogs and rendered in front of the Page Routes */}
      <WouterRouter base={APP_BASE} hook={useDialogLocation}>
        <Switch>
          <Route path={DialogRoutes.profile}>
            <Protected needsOrg={false}>
              <DialogLayout isOpen>
                <Profile />
              </DialogLayout>
            </Protected>
          </Route>
          <DefaultDialogRoute path={DialogRoutes.organizationMembers}>
            <OrganizationMembers />
          </DefaultDialogRoute>
          <DefaultDialogRoute path={DialogRoutes.organizationBilling}>
            <OrganizationBilling />
          </DefaultDialogRoute>
          <DefaultDialogRoute path={DialogRoutes.organizationSettings}>
            <OrganizationSettings />
          </DefaultDialogRoute>
          <DefaultDialogRoute path={DialogRoutes.goodie}>
            <GoodieSuspense />
          </DefaultDialogRoute>
          <DefaultDialogRoute path={DialogRoutes.promotion}>
            <PromotionSuspense />
          </DefaultDialogRoute>
          <DefaultDialogRoute path={DialogRoutes.category}>
            <CategorySuspense />
          </DefaultDialogRoute>
          <DefaultDialogRoute path={DialogRoutes.editEventOccurence}>
            <EditEventOccurenceSuspense />
          </DefaultDialogRoute>
          <DefaultDialogRoute path={DialogRoutes.stay}>
            <StaySuspense />
          </DefaultDialogRoute>
          <DefaultDialogRoute path={DialogRoutes.space}>
            <SpaceSuspense />
          </DefaultDialogRoute>
          <DefaultDialogRoute path={DialogRoutes.stayOrders}>
            <StayOrdersSuspense />
          </DefaultDialogRoute>
          <DefaultDialogRoute path={DialogRoutes.guest}>
            <GuestSuspense />
          </DefaultDialogRoute>
          <DefaultDialogRoute path={DialogRoutes.integration}>
            <IntegrationSuspense />
          </DefaultDialogRoute>
          <DefaultDialogRoute path={DialogRoutes.addCategoryGoodies}>
            <CheckPermission permissions={[Permission.LAYOUT_UPDATE]}>
              <AddGoodiesToCategory />
            </CheckPermission>
          </DefaultDialogRoute>
          <DefaultDialogRoute path={DialogRoutes.createPromotion}>
            <CheckPermission permissions={[Permission.PROMOTION_CREATE]}>
              <CreatePromotion />
            </CheckPermission>
          </DefaultDialogRoute>
          <DefaultDialogRoute path={DialogRoutes.createGoodie}>
            <CheckPermission permissions={[Permission.GOODIE_CREATE]}>
              <CreateGoodie />
            </CheckPermission>
          </DefaultDialogRoute>
          <DefaultDialogRoute path={DialogRoutes.createCategory}>
            <CheckPermission permissions={[Permission.LAYOUT_UPDATE]}>
              <CreateCategory />
            </CheckPermission>
          </DefaultDialogRoute>
          <DefaultDialogRoute path={DialogRoutes.createStay}>
            <CheckPermission permissions={[Permission.STAY_CREATE]}>
              <CheckPms>
                <CreateStay />
              </CheckPms>
            </CheckPermission>
          </DefaultDialogRoute>
          <DefaultDialogRoute path={DialogRoutes.createSpace}>
            <CheckPermission permissions={[Permission.SPACE_CREATE]}>
              <CheckPms>
                <CreateSpace />
              </CheckPms>
            </CheckPermission>
          </DefaultDialogRoute>
        </Switch>
      </WouterRouter>
    </WouterRouter>
  )
}
